import { logger } from '~/src/common/services/Logger';
import { isTruthy } from '~/src/common/utils/guards';

import { AutocompleteSessionToken } from './types';

const getAutocompleteSuggestionService = () => {
  if (typeof window === 'undefined') {
    throw new Error('google.maps.places.AutocompleteSuggestion cannot be used on server side');
  }

  if (!window.google) {
    throw new Error('window.google is not defined');
  }

  return google.maps.places.AutocompleteSuggestion;
};

const suggest = async (
  input: string,
  options: {
    types: string[];
    sessionToken?: AutocompleteSessionToken;
  },
) => {
  const Service = getAutocompleteSuggestionService();

  const FRANCE_METROPOLITAN_BOUNDS = new google.maps.LatLngBounds(
    new google.maps.LatLng(41.303, -5.142), // Sud-Ouest
    new google.maps.LatLng(51.124, 9.561), // Nord-Est
  );

  const results = await Service.fetchAutocompleteSuggestions({
    input,
    locationRestriction: FRANCE_METROPOLITAN_BOUNDS,
    includedPrimaryTypes: options.types,
    sessionToken: options.sessionToken,
  });

  const detailsPromises = results.suggestions.map(async suggestion => {
    try {
      const place = suggestion.placePrediction?.toPlace();
      const details = await place?.fetchFields({
        fields: ['formattedAddress', 'addressComponents', 'location'],
      });

      return {
        matches: suggestion.placePrediction?.mainText?.matches,
        details,
      };
    } catch (err) {
      logger.error(
        `Failed to fetch place details for ${suggestion.placePrediction?.placeId ?? ''}`,
        {
          originalError: err,
        },
      );
      return null;
    }
  });

  const formattedSuggestions = (await Promise.all(detailsPromises))
    .flatMap(s => (s?.details?.place ? { place: s.details?.place, matches: s.matches } : null))
    .filter(isTruthy);

  return formattedSuggestions;
};

const getAutocompleteSessionToken = () => {
  if (typeof window === 'undefined') {
    throw new Error('google.maps.places.AutocompleteSessionToken cannot be used on server side');
  }

  if (!window.google) {
    throw new Error('window.google is not defined');
  }

  return new google.maps.places.AutocompleteSessionToken();
};

const GoogleAutocomplete = {
  getAutocompleteSessionToken,
  getPlacePredictions: suggest,
};

export default GoogleAutocomplete;
